import React, { forwardRef, useRef, useState, useEffect, useCallback, useMemo } from "react";
import { highlightQueryString } from "@/utils/elasticsearch-query-parser";
import { useMentionAutocomplete, MentionField } from "@/hooks/use-mention-autocomplete";
import { FieldDropdown } from "@/components/field-selector/field-dropdown";
import { AvailableFilter } from "@/models/filter";
import { isPrefixedField, getDisplayName } from "@/utils/fields-mapping";

/**
 * Map ES types to prefix types
 */
const esTypeToPrefix: Record<string, string> = {
  text: "text",
  keyword: "text",
  long: "num",
  integer: "num",
  short: "num",
  byte: "num",
  double: "num",
  float: "num",
  boolean: "bool",
  date: "date",
  object: "obj",
  nested: "obj",
};

/**
 * Regex to match prefixed fields in internal value
 */
const PREFIXED_FIELD_REGEX = /((?:num|text|bool|date|obj)\$[a-zA-Z_][a-zA-Z0-9_.]*)/g;

/**
 * Convert internal value (with type$field) to display value (field only)
 * Also returns a mapping of display fields to their internal values
 */
const internalToDisplay = (internalValue: string): { displayValue: string; fieldMap: Map<string, string> } => {
  const fieldMap = new Map<string, string>();
  
  const displayValue = internalValue.replace(PREFIXED_FIELD_REGEX, (match) => {
    const displayName = getDisplayName(match);
    fieldMap.set(displayName, match);
    return displayName;
  });
  
  return { displayValue, fieldMap };
};

/**
 * Convert display value back to internal value using the field mapping
 */
const displayToInternal = (displayValue: string, fieldMap: Map<string, string>): string => {
  let result = displayValue;
  
  // Sort by length descending to replace longer matches first
  const sortedEntries = Array.from(fieldMap.entries()).sort((a, b) => b[0].length - a[0].length);
  
  for (const [displayName, internalName] of sortedEntries) {
    // Use word boundary matching to avoid partial replacements
    const regex = new RegExp(`(?<![a-zA-Z0-9_.$])${escapeRegex(displayName)}(?![a-zA-Z0-9_])`, 'g');
    result = result.replace(regex, internalName);
  }
  
  return result;
};

/**
 * Escape special regex characters
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export interface LuceneTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  leftIcon?: React.ReactNode;
  fullWidth?: boolean;
  variant?: "default" | "filled" | "ghost";
  /** Minimum number of rows */
  minRows?: number;
  /** Maximum number of rows before scrolling */
  maxRows?: number;
  /** Available fields for @ mention autocomplete */
  availableFields?: MentionField[];
  /** Callback when a field is selected via @ mention */
  onFieldMention?: (field: MentionField) => void;
}

/**
 * Textarea component with Lucene syntax highlighting and @ field mention
 * 
 * Uses two values internally:
 * - Internal value: Contains prefixed fields (e.g., "num$stats.likes") for backend
 * - Display value: Shows clean field names (e.g., "stats.likes") to user
 *
 * Features:
 * - Lucene query syntax highlighting (fields, operators, values, etc.)
 * - @ mention autocomplete for field names
 * - Field chips styling without cursor misalignment
 * - Multiple variants: default, filled, ghost
 * - Multiple sizes: sm, md, lg
 */
const LuceneTextarea = forwardRef<HTMLTextAreaElement, LuceneTextareaProps>(
  (
    {
      label,
      error,
      helperText,
      size = "md",
      leftIcon,
      fullWidth = true,
      variant = "default",
      className = "",
      disabled = false,
      required = false,
      id,
      value,
      onChange,
      minRows = 3,
      maxRows = 10,
      availableFields,
      onFieldMention,
      ...props
    },
    ref
  ) => {
    const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
    const highlightRef = useRef<HTMLDivElement>(null);
    const mentionListRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    
    // Field mapping for conversion between display and internal values
    const fieldMapRef = useRef<Map<string, string>>(new Map());

    // Sync refs
    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalTextareaRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      },
      [ref]
    );

    // Internal value from props (with type$field format)
    const internalValue = typeof value === "string" ? value : "";
    
    // Convert internal value to display value
    const { displayValue, fieldMap } = useMemo(() => {
      return internalToDisplay(internalValue);
    }, [internalValue]);
    
    // Update field map ref when it changes
    useEffect(() => {
      fieldMapRef.current = fieldMap;
    }, [fieldMap]);

    const hasError = !!error;

    // Mention autocomplete hook
    const {
      mentionState,
      filteredFields,
      handleInputChange: handleMentionInput,
      handleKeyDown: handleMentionKeyDown,
      selectField,
      closeMention,
    } = useMentionAutocomplete({
      availableFields,
      onSelect: onFieldMention,
    });

    // Size styles
    const sizeStyles = {
      sm: {
        textarea: "py-1.5 text-sm leading-5",
        paddingLeft: leftIcon ? "pl-9" : "pl-3",
        paddingRight: "pr-3",
        icon: "text-sm",
        iconLeft: "left-3 top-2",
        label: "text-xs mb-1.5",
        lineHeight: "1.25rem",
      },
      md: {
        textarea: "py-2.5 text-sm leading-5",
        paddingLeft: leftIcon ? "pl-10" : "pl-4",
        paddingRight: "pr-4",
        icon: "text-base",
        iconLeft: "left-3.5 top-3",
        label: "text-sm mb-2",
        lineHeight: "1.25rem",
      },
      lg: {
        textarea: "py-3.5 text-base leading-6",
        paddingLeft: leftIcon ? "pl-12" : "pl-5",
        paddingRight: "pr-5",
        icon: "text-lg",
        iconLeft: "left-4 top-4",
        label: "text-sm mb-2",
        lineHeight: "1.5rem",
      },
    };

    const currentSize = sizeStyles[size];

    // Variant styles
    const variantStyles = {
      default: `
        bg-surface-elevated
        border border-arc-border
        hover:border-arc-border-strong
      `,
      filled: `
        bg-surface-secondary
        border border-transparent
        hover:bg-surface-tertiary
      `,
      ghost: `
        bg-transparent
        border border-transparent
        hover:bg-surface-secondary
      `,
    };

    // Focus styles
    const focusStyles = isFocused ? "border-transparent ring-2 ring-accent" : "";

    // Base textarea styles
    const textareaBaseStyles = `
      w-full
      rounded-arc-md
      font-medium
      placeholder:text-content-tertiary placeholder:font-normal
      transition-all duration-arc-normal ease-arc
      outline-none
      resize-none
      disabled:bg-surface-tertiary disabled:text-content-tertiary disabled:cursor-not-allowed disabled:border-arc-border
    `;

    const errorStyles = hasError
      ? "border-error hover:border-error focus:border-error focus:ring-error-main/20"
      : "";

    // Synchronize scroll between textarea and highlight overlay
    const handleScroll = useCallback(() => {
      if (internalTextareaRef.current && highlightRef.current) {
        highlightRef.current.scrollTop = internalTextareaRef.current.scrollTop;
        highlightRef.current.scrollLeft = internalTextareaRef.current.scrollLeft;
      }
    }, []);

    // Calculate min and max height based on rows
    const calculateHeight = useCallback(() => {
      const lineHeightPx = parseFloat(currentSize.lineHeight) * 16;
      const paddingY = size === "sm" ? 12 : size === "md" ? 20 : 28;
      const minHeight = minRows * lineHeightPx + paddingY;
      const maxHeight = maxRows * lineHeightPx + paddingY;
      return { minHeight, maxHeight };
    }, [currentSize.lineHeight, size, minRows, maxRows]);

    const { minHeight, maxHeight } = calculateHeight();

    // Get highlighted parts for the display value
    const highlightedParts = highlightQueryString(displayValue);

    // Sync scroll on value change
    useEffect(() => {
      handleScroll();
    }, [displayValue, handleScroll]);

    // Handle change - convert display value back to internal value
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newDisplayValue = e.target.value;
        const cursorPosition = e.target.selectionStart || 0;

        // Check for mention trigger
        handleMentionInput(newDisplayValue, cursorPosition, internalTextareaRef.current);

        // Convert display value back to internal value
        const newInternalValue = displayToInternal(newDisplayValue, fieldMapRef.current);

        // Call original onChange with internal value
        if (onChange) {
          const syntheticEvent = {
            ...e,
            target: { ...e.target, value: newInternalValue },
            currentTarget: { ...e.currentTarget, value: newInternalValue },
          } as React.ChangeEvent<HTMLTextAreaElement>;
          onChange(syntheticEvent);
        }
      },
      [onChange, handleMentionInput]
    );

    // Handle field selection
    const doSelectField = useCallback(
      (field: MentionField) => {
        if (!onChange) return;
        
        // Build the internal field value with type prefix.
        // Only add prefix for simple field names (no dots) that aren't already prefixed.
        // Fields with dots (e.g. "extra.sentiment") are facet-derived paths that the
        // backend transform_query_string will resolve to their full prefixed ES path.
        let internalFieldValue = field.key;
        if (!isPrefixedField(field.key) && !field.key.includes(".")) {
          const prefix = esTypeToPrefix[field.type.toLowerCase()] || "text";
          internalFieldValue = `${prefix}$${field.key}`;
        }
        
        // Get display name for insertion into textarea (with ":" suffix for Lucene syntax)
        const displayFieldName = getDisplayName(internalFieldValue);
        const displayFieldWithColon = `${displayFieldName}:`;
        
        // Update the field map with the new field (without colon for matching)
        fieldMapRef.current.set(displayFieldName, internalFieldValue);
        
        // Insert display name with colon into current display value
        const newDisplayValue = selectField(
          { key: displayFieldWithColon, type: field.type },
          displayValue
        );
        
        // Convert back to internal value
        const newInternalValue = displayToInternal(newDisplayValue, fieldMapRef.current);
        
        // Create synthetic event with internal value
        const syntheticEvent = {
          target: { value: newInternalValue },
          currentTarget: { value: newInternalValue },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(syntheticEvent);

        // Move cursor to end of inserted field (after the colon)
        requestAnimationFrame(() => {
          if (internalTextareaRef.current) {
            const cursorPos = mentionState.startIndex + displayFieldWithColon.length;
            internalTextareaRef.current.setSelectionRange(cursorPos, cursorPos);
            internalTextareaRef.current.focus();
          }
        });
      },
      [selectField, displayValue, onChange, mentionState.startIndex]
    );

    // Handle keydown for mention navigation
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const handled = handleMentionKeyDown(e);

        if (
          handled &&
          (e.key === "Enter" || e.key === "Tab") &&
          filteredFields.length > 0
        ) {
          const selectedField = filteredFields[mentionState.highlightedIndex];
          if (selectedField) {
            doSelectField(selectedField);
          }
        }

        if (!handled) {
          props.onKeyDown?.(e);
        }
      },
      [
        handleMentionKeyDown,
        filteredFields,
        mentionState.highlightedIndex,
        doSelectField,
        props,
      ]
    );

    // Close mention on blur
    const handleBlur = useCallback(() => {
      setIsFocused(false);
      setTimeout(() => {
        closeMention();
      }, 150);
    }, [closeMention]);

    // Convert fields for dropdown
    const availableFiltersForDropdown: AvailableFilter[] = useMemo(() => {
      return filteredFields.map((field) => ({
        key: field.key,
        type: field.type,
        enabled: true,
      }));
    }, [filteredFields]);

    const highlightedFieldKey = useMemo(() => {
      if (filteredFields.length > 0 && mentionState.highlightedIndex < filteredFields.length) {
        return filteredFields[mentionState.highlightedIndex]?.key;
      }
      return undefined;
    }, [filteredFields, mentionState.highlightedIndex]);

    const handleDropdownFieldSelect = useCallback(
      (field: AvailableFilter) => {
        const mentionField: MentionField = { key: field.key, type: field.type };
        doSelectField(mentionField);
      },
      [doSelectField]
    );

    return (
      <div className={`${fullWidth ? "w-full" : ""} ${className}`}>
        {label && (
          <label
            htmlFor={id}
            className={`block font-semibold text-content-primary ${currentSize.label}`}
          >
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div
              className={`absolute ${currentSize.iconLeft} ${currentSize.icon} text-content-tertiary pointer-events-none z-10`}
            >
              {leftIcon}
            </div>
          )}

          {/* Container for textarea and overlay */}
          <div
            className={`
              relative
              rounded-arc-md
              ${variantStyles[variant]}
              ${focusStyles}
              ${errorStyles}
            `}
          >
            {/* Highlight overlay */}
            <div
              ref={highlightRef}
              className={`
                absolute inset-0
                pointer-events-none
                overflow-hidden
                rounded-arc-md
                ${currentSize.textarea}
                ${currentSize.paddingLeft}
                ${currentSize.paddingRight}
              `}
              style={{
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                minHeight,
                maxHeight,
              }}
              aria-hidden="true"
            >
              {highlightedParts.map((part, index) =>
                part.isChip ? (
                  // Render field chips with styling (no padding to keep cursor aligned)
                  <span
                    key={index}
                    className="text-accent bg-accent/10 border-b-2 border-accent"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {part.text}
                  </span>
                ) : (
                  <span
                    key={index}
                    className={part.className || "text-content-primary"}
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {part.text}
                  </span>
                )
              )}
              {displayValue && !displayValue.endsWith("\n") && <span>&nbsp;</span>}
            </div>

            {/* Actual textarea - transparent text */}
            <textarea
              ref={setRefs}
              id={id}
              disabled={disabled}
              required={required}
              value={displayValue}
              onChange={handleChange}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              onScroll={handleScroll}
              onKeyDown={handleKeyDown}
              className={`
                ${textareaBaseStyles}
                ${currentSize.textarea}
                ${currentSize.paddingLeft}
                ${currentSize.paddingRight}
                text-transparent
                caret-accent
                selection:bg-accent/20
                bg-transparent
                relative
                z-[1]
                border-0
                focus:border-0
                focus:ring-0
                focus:outline-none
              `}
              style={{
                minHeight,
                maxHeight,
                caretColor: "var(--arc-accent)",
                border: "none",
                boxShadow: "none",
              }}
              {...props}
            />
          </div>
        </div>

        {(error || helperText) && (
          <p
            className={`mt-1.5 text-xs ${
              hasError ? "text-error" : "text-content-tertiary"
            }`}
          >
            {error || helperText}
          </p>
        )}

        {/* Mention dropdown */}
        {mentionState.isOpen && availableFiltersForDropdown.length > 0 && (
          <FieldDropdown
            availableFields={availableFiltersForDropdown}
            position={mentionState.position}
            width={300}
            maxHeight={300}
            onFieldSelect={handleDropdownFieldSelect}
            onClose={closeMention}
            highlightedKey={highlightedFieldKey}
            searchQuery={mentionState.query}
            dropdownRef={mentionListRef as React.RefObject<HTMLDivElement>}
          />
        )}
      </div>
    );
  }
);

LuceneTextarea.displayName = "LuceneTextarea";

export default LuceneTextarea;
